// Chi sei e cosa puoi toccare (2026-08-03).
//
// ⚠️ Questa è COSMESI. La difesa vera sono le guardie sui router del backend
// (`modules/auth/permessi.py`): qui si evita solo di mostrare tasti che
// darebbero 403. Se un giorno queste due fonti divergono, quella giusta è il
// backend — e l'utente vede un errore, non un dato che non doveva vedere.
//
// Si chiama `/api/auth/me` UNA volta e si condivide via context: ogni pagina
// che chiede i permessi da sé farebbe una fetch per montaggio.
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { apiGet } from "@/shared/api";
import type { AuthUser } from "@/features/auth/api";
import { AttivaMfaPage } from "@/features/auth/AttivaMfaPage";

/** Aree di scrittura, allineate a `auth/permessi.py`. */
export type Area =
  | "ordini"
  | "fatturazione"
  | "clienti"
  | "resi"
  | "catalogo"
  | "fornitori"
  | "mail"
  | "spedizioni"
  | "comune"
  | "sistema"
  | "account"
  | "impostazioni";

export const RUOLO_GOD = "god";

type MeResponse = { user: AuthUser; aree_scrivibili: Area[]; mfa_da_attivare?: boolean };

interface Permessi {
  utente: AuthUser | null;
  aree: Area[];
  /** `true` finché `/me` non ha risposto: evita di sfarfallare i controlli. */
  inCaricamento: boolean;
  puoScrivere: (area: Area) => boolean;
  isGod: boolean;
}

const VUOTO: Permessi = {
  utente: null,
  aree: [],
  inCaricamento: true,
  puoScrivere: () => false,
  isGod: false,
};

const PermessiContext = createContext<Permessi>(VUOTO);

export function PermessiProvider({ children }: { children: ReactNode }) {
  const [utente, setUtente] = useState<AuthUser | null>(null);
  const [aree, setAree] = useState<Area[]>([]);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [mfaDaAttivare, setMfaDaAttivare] = useState(false);

  useEffect(() => {
    let vivo = true;
    apiGet<MeResponse>("/api/auth/me")
      .then((me) => {
        if (!vivo) return;
        setUtente(me.user);
        setAree(me.aree_scrivibili ?? []);
        setMfaDaAttivare(Boolean(me.mfa_da_attivare));
      })
      // 401 = non loggato: ci pensa il redirect al login in `shared/api`.
      // Qui non si alza nulla, altrimenti l'app non monta la pagina di login.
      .catch(() => {})
      .finally(() => {
        if (vivo) setInCaricamento(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const valore = useMemo<Permessi>(() => {
    const isGod = utente?.role === RUOLO_GOD;
    return {
      utente,
      aree,
      inCaricamento,
      isGod,
      // 🔴 Mentre `/me` è in volo si nega. Il contrario mostrerebbe i tasti
      // attivi per un istante a chi non può usarli: un click in quella
      // finestra parte davvero e torna 403.
      puoScrivere: (area: Area) => (isGod ? true : aree.includes(area)),
    };
  }, [utente, aree, inCaricamento]);

  // Secondo fattore obbligatorio (DPP Amazon): finché non è attivo il backend
  // risponde 403 a tutto il resto, quindi si mostra SOLO l'attivazione.
  if (mfaDaAttivare) return <AttivaMfaPage />;

  return <PermessiContext.Provider value={valore}>{children}</PermessiContext.Provider>;
}

export function usePermessi(): Permessi {
  return useContext(PermessiContext);
}

/**
 * Props da spalmare su un controllo di scrittura: lo disabilita e SPIEGA
 * perché. Un tasto sparito non dice niente; uno disabilitato con motivo sì —
 * ed è la regola del progetto (ogni pulsante deve dare un feedback).
 */
export function propsSenzaPermesso(puo: boolean, area: Area): { disabled?: true; title?: string } {
  if (puo) return {};
  return {
    disabled: true,
    title: `Non hai i permessi per operare nell'area «${area}». Puoi consultarla, ma non modificarla.`,
  };
}
